import React, { useState, useRef, useEffect } from 'react';
import { useOasisBackend, BACKEND_URL} from '../../hooks/useOasisBackend';
import type { ChatMessage, DatabaseList, ReadDatabase } from '../../api/OasisBackendApi';
import UnifiedSearchBar from './unified-search/UnifiedSearchBar';
import Message from './Message';
import Markdown from "react-markdown";
import ReconnectingWebSocket from 'reconnecting-websocket';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useUser } from '../../hooks/useUser';
import { useLoggedOutMessageCounter } from '../hooks/useLoggedOutMessageCounter';
import DomainSelector from './DomainSelector';

interface ChatWindowProps {
  initialMessage?: string;
  resetKey: number;
  messages: ChatMessage[];
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  conversationId: number | null;
  setConversationId: (conversationId: number | null) => void;
  onFirstInteraction?: () => void;
  isExpanded?: boolean;
  onConversationIdChange?: (conversationId: number) => void;
  onMessageSent?: (message: string, conversationId: number | null) => boolean;
  currentAgent?: string;
  onAgentChange?: (agent: string) => void;
  currentModel?: string;
  onModelChange?: (model: string) => void;
  agentParams?: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean };
  onAgentParamsChange?: (params: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean }) => void;
  showDomainSelector?: boolean;
  onDomainSelect?: (id: string) => void;
  showMultiDomainSelector?: boolean;
  onDomainsSelect?: (domains: { id: string; name: string }[]) => void;
  onSearch?: (query: string, params: { model: string; agent: string; agentParams: { domain_id: string | string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean } }) => void;
}

// Frontend message interface with strict role types
interface RenderMessage {
  content: string;
  role: 'user' | 'assistant' | 'system' | 'ui_component' | 'block_update' | 'reasoning';
  type?: string;
  data?: any;
  reasoning_type?: 'text' | 'summary';
  reasoning_duration?: number;  // If present (including 0), reasoning is complete
}

interface WebSocketMessage {
  type: 'content' | 'ui_component' | 'done' | 'error' | 'block_update' | 'reasoning';
  content?: string;
  conversation_id?: number;
  error?: string;
  data?: {
    id?: string;
    type?: string;
    data?: any;
    result?: any;
  };
  loading?: boolean;
  reasoning_type?: 'text' | 'summary';
  reasoning_duration?: number;  // Sent by backend when reasoning completes
}


const ChatWindow: React.FC<ChatWindowProps> = ({ 
  messages, 
  setMessages, 
  conversationId, 
  setConversationId, 
  initialMessage, 
  resetKey, 
  onFirstInteraction, 
  isExpanded = false, 
  onConversationIdChange = () => {}, 
  onMessageSent, 
  currentAgent = 'search_engine', 
  onAgentChange, 
  currentModel = 'gpt-4.1-mini', 
  onModelChange,
  agentParams, 
  onAgentParamsChange,
  onSearch
}) => {
  const navigate = useNavigate();
  const db = useOasisBackend();
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const hasHandledReset = useRef<number | null>(null);
  const hasTrackedFirstInteraction = useRef(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [bottomPadding, setBottomPadding] = useState(0);
  const [accumulatedUIComponent, setAccumulatedUIComponent] = useState<{ type: string; data: any } | null>(null);
  const pendingInitialMessage = useRef<string | null>(null);
  const { ws, isWsReady } = useWebSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementMessageCount, LimitReachedMessage, hasReachedLimit, showLimitReached, setShowLimitReached, isInitialized } = useLoggedOutMessageCounter(loggedIn);
  const [showSearchingIndicator, setShowSearchingIndicator] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout>();
  const [domains, setDomains] = useState<ReadDatabase[] | null>(null);



  // Convert backend messages to frontend render messages
  const renderMessages: RenderMessage[] = messages.map(msg => ({
    content: msg.content,
    role: msg.role as 'user' | 'assistant' | 'system' | 'ui_component' | 'block_update' | 'reasoning',
    reasoning_type: (msg as any).reasoning_type,
    reasoning_duration: (msg as any).reasoning_duration,
  }));

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await db.endpoints.rhea.rheaApiRheaAccountDatabasesRetrieve();
        const databaseList = response.data as DatabaseList;
        setDomains(databaseList.databases as ReadDatabase[]);
      } catch (error) {
        console.error('Error fetching domains:', error);
        setDomains(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDomains();
  }, []);

  // Scroll to position the last user message at the top of the viewport
  useEffect(() => {
    if (shouldScroll && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const lastUserMessage = container.querySelector('.justify-end');
      
      if (lastUserMessage) {
        // 1. Measure chat window height
        const containerHeight = container.clientHeight;
        console.log("Container Height", containerHeight);
        
        // 2. Measure last user message height
        const messageHeight = lastUserMessage.clientHeight;
        console.log("Message Height", messageHeight);
        
        // 3. Calculate and set padding (subtract 16px for message spacing)
        const padding = containerHeight - messageHeight - 50;
        setBottomPadding(padding);
        console.log("Padding", padding);

        // Wait for padding to be rendered before scrolling
        setTimeout(() => {
          // 4. Scroll to bottom to show padding and message
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      }
      setShouldScroll(false);
    }
  }, [messages, shouldScroll]);

  // Scroll to bottom and remove padding when message is complete
  useEffect(() => {
    if (shouldScrollToBottom && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      
      // 6. Remove padding
      setBottomPadding(0);
      
      // 7. Scroll to bottom
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
      
      setShouldScrollToBottom(false);
    }
  }, [messages, shouldScrollToBottom]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      const searchBar = document.querySelector('.chat-input input[type="text"]') as HTMLInputElement;
      if (searchBar) {
        searchBar.focus();
      }
    }
  }, [isExpanded, messages.length]);

  // Reset chat when resetKey changes
  useEffect(() => {
    if (resetKey !== hasHandledReset.current) {
      hasHandledReset.current = resetKey;
      setMessages([]);
      console.log("ChatWindow resetKey", resetKey);
      setConversationId(null);
      hasTrackedFirstInteraction.current = false;
      
      if (initialMessage) {
        console.log("Initial Message", initialMessage, resetKey);
        if (isWsReady && agentParams && agentParams.domain_id) {
          handleSendMessage(initialMessage);
        } else {
          pendingInitialMessage.current = initialMessage;
        }
      }
    }
  }, [resetKey, isWsReady, conversationId, agentParams]);

  // WebSocket message handling
  useEffect(() => {
    if (!ws.current) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;

        if (data.type === 'error' || data.error) {
          console.error('WebSocket error:', data.error);
          return;
        }

        if (data.conversation_id && !conversationId) {
          setConversationId(data.conversation_id);
            // Update URL properly using navigation instead of searchParams
          onConversationIdChange(data.conversation_id);
        }
        console.log("WebSocket message:", data);

        switch (data.type) {
          case 'reasoning':
            // Handle reasoning chunks from reasoning models
            setMessages(prev => {
              const lastMessage = prev[prev.length - 1];
              
              // If duration is provided, this is the completion signal - update last reasoning message
              if (data.reasoning_duration !== undefined) {
                if (lastMessage?.role === 'reasoning') {
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      reasoning_duration: data.reasoning_duration
                    }
                  ];
                }
                // If no reasoning message exists, create one (edge case)
                return [...prev, {
                  role: 'reasoning',
                  content: '',
                  reasoning_type: data.reasoning_type || 'text',
                  reasoning_duration: data.reasoning_duration
                }];
              }
              
              // Otherwise, it's a streaming chunk - append content
              if (data.content) {
                if (lastMessage?.role === 'reasoning' && (lastMessage as any).reasoning_duration === undefined) {
                  // Append to existing reasoning message (still streaming)
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      content: (lastMessage.content || '') + data.content!
                    }
                  ];
                } else {
                  // Create new reasoning message (no duration = still streaming)
                  return [...prev, { 
                    role: 'reasoning', 
                    content: data.content!,
                    reasoning_type: data.reasoning_type || 'text'
                  }];
                }
              }
              
              return prev;
            });
            break;
          case 'content':
            if (data.content) {
              setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                
                if (lastMessage?.role === 'assistant') {
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      content: (lastMessage.content || '') + data.content!
                    }
                  ];
                } else {
                  return [...prev, { role: 'assistant', content: data.content! }];
                }
              });
            }
            break;
          case 'ui_component':
            console.log("UI COMPONENT DATA", data);
            if (data.content) {
              try {
                const uiComponentData = JSON.parse(data.content);
                console.log("UI COMPONENT DATA", uiComponentData);
                if (uiComponentData.data && typeof uiComponentData.data === 'string') {
                  uiComponentData.data = JSON.parse(uiComponentData.data);
                }
                setMessages(prev => [...prev, {
                  role: 'ui_component',
                  content: JSON.stringify(uiComponentData),
                }]);
              } catch (err) {
                console.error('Error parsing UI component data:', err);
              }
            }
            break;
          case 'block_update':
            console.log('🔧 Block Update:', data.data?.id, data);
            setMessages(messages => messages.map(message => {
              console.log("Message", message);
              if (message.role === 'ui_component') {
                let content:any = message.content;
                if (typeof message.content === 'string') {
                  content = JSON.parse(message.content);
                }
                let results = content.data.results.map((result: any) => {
                  if (result.id === data.data?.id) {
                    return {
                      ...result,
                      id: data.data?.id,
                      type: data.data?.type,
                      loading: data.loading,
                      data: JSON.parse(data.data?.result)
                    }
                  }
                  return result;
                });
                content.data = {
                  ...content.data,
                  results: results
                };
                console.log("New Content", content);
                return {
                  role: 'ui_component',
                  content: JSON.stringify(content)
                }
              }
              return message;
            }));
            break;
          case 'done':
            // Backend should have already sent reasoning_duration before done
            setIsLoading(false);
            setShowSearchingIndicator(false);
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }
            setShouldScrollToBottom(true);
            break;
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err);
      }
    };

    ws.current.onmessage = handleMessage;

    return () => {
      if (ws.current) {
        ws.current.onmessage = null;
      }
    };
  }, [conversationId, onConversationIdChange]);

  // Handle loading indicator timing
  useEffect(() => {
    if (isLoading) {
      loadingTimeoutRef.current = setTimeout(() => {
        setShowSearchingIndicator(true);
      }, 1000);
    } else {
      setShowSearchingIndicator(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [isLoading]);

  // Handle pending initial message when WebSocket becomes ready
  useEffect(() => {
    if (isWsReady && pendingInitialMessage.current && agentParams && agentParams.domain_id) {
      console.log("Sending pending initial message:", pendingInitialMessage.current);
      handleSendMessage(pendingInitialMessage.current);
      pendingInitialMessage.current = null;
    }
  }, [isWsReady, agentParams]);

  const handleSearch = (query: string, params: { model: string; agent: string; agentParams: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean } }) => {

    
    console.log("ChatWindow handleSearch called with:", { query, params });
    
    // If parent provided onSearch callback, use it
    if (onSearch) {
      onSearch(query, params);
      return;
    }

    // Handle search_engine agent - redirect to SearchEngine
    if (params.agent === 'search_engine') {
      const searchParams = new URLSearchParams();
      searchParams.set('q', query);
      searchParams.set('model', params.model);
      searchParams.set('agent', params.agent);
      
      // Add all agent parameters to URL
      Object.entries(params.agentParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            searchParams.set(key, value.join(','));
          } else {
            searchParams.set(key, String(value));
          }
        }
      });

      console.log('ChatWindow: Redirecting to search with params:', {
        query,
        model: params.model,
        agent: params.agent,
        agentParams: params.agentParams,
        url: `/search?${searchParams.toString()}`
      });

      navigate(`/search?${searchParams.toString()}`);
      return;
    }

    // For other agents, send message via WebSocket
    handleSendMessage(query, params);
  };

  const handleSendMessage = async (message: string, params?: { model: string; agent: string; agentParams: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean } }) => {
    if (isLoading) return;
    console.log("handleSendMessage called with:", message);
    console.log("WebSocket state:", { wsExists: !!ws.current, isWsReady, wsState: ws.current?.readyState });
    
    if (!message.trim() || !ws.current) {
      console.log("Not sending message - empty or no WebSocket connection");
      return;
    }

    if (!loggedIn && hasReachedLimit) {
      setShowLimitReached(true);
      return;
    }

    if (!loggedIn) {
      incrementMessageCount();
    }

    // Track first interaction if this is not the initial auto-message and we haven't tracked yet
    if (!hasTrackedFirstInteraction.current && onFirstInteraction && message !== initialMessage) {
      hasTrackedFirstInteraction.current = true;
      onFirstInteraction();
      console.log("Called onFirstInteraction");
    }

    // Call onMessageSent and check if we should continue
    const shouldStop = onMessageSent?.(message, conversationId);
    console.log("onMessageSent returned:", shouldStop);
    if (shouldStop) {
      console.log("Message sent, not sending to WebSocket");
      return;
    }

    setIsLoading(true);
    setMessages((prev: ChatMessage[]) => [...prev, { role: 'user', content: message }]);
    console.log("Sending message:", message);
    setShouldScroll(true);

    try {
      const messageData: any = {
        message,
        model: params?.model || currentModel,
        conversation_id: conversationId,
        agent_slug: params?.agent || currentAgent,
        stream: true
      };

      // Add agent parameters if present
      if (params?.agentParams) {
        messageData.agent_params = params.agentParams;
      }

      console.log("Sending WebSocket message:", messageData);
      ws.current.send(JSON.stringify(messageData));
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
    }
  };

  const getLastAssistantMessage = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return messages[i].content;
      }
    }
    return 'No response yet';
  };

  return (
    <div 
      ref={chatRef}
      className={`rounded-lg shadow-lg transition-all duration-300 ease-in-out hover:border border-none ${
        isExpanded 
          ? 'w-full h-full' 
          : 'w-full h-50 cursor-pointer hover:shadow-xl'
      }`}
      style={{ 
        backgroundColor: 'var(--rhea-bg-primary)',
        border: '1px solid var(--rhea-card-border)'
      }}
    >
      <LimitReachedMessage />
      {/* Content */}
      <div className="h-full flex flex-col">
        {isExpanded ? (
          <>
            {/* Messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {renderMessages.map((message, index) => (
                <Message
                  key={index}
                  content={message.content}
                  role={message.role}
                  isCollapsed={false}
                  reasoning_type={message.reasoning_type}
                  reasoning_duration={message.reasoning_duration}
                />
              ))}
              {showSearchingIndicator && (
                <div className="flex justify-start">
                  <div className="bg-inherit rounded-lg p-3 animate-pulse" style={{ borderColor: 'var(--rhea-card-border)' }}>
                    <div className="text-sm" style={{ color: 'var(--rhea-text-primary)' }}>
                      Searching for you...
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
              {/* Dynamic padding */}
              <div style={{ height: bottomPadding }} />
            </div>

            {/* Input Form */}
            <div className="p-1 sm:p-4">
              <UnifiedSearchBar
                onSearch={handleSearch}
                isLoading={isLoading}
                initialPlaceholderMessage="Type your message..."
                autoFocus={true}
                className="chat-input"
                initialModel={currentModel}
                initialAgent={currentAgent}
                initialAgentParams={agentParams}
                domains={domains}
                onModelChange={onModelChange}
                onAgentChange={onAgentChange}
                onAgentParamsChange={onAgentParamsChange}
                conversationId={conversationId ? conversationId : undefined}
              />
            </div>
          </>
        ) : (
          // Show last message in a proper message bubble when collapsed
          <div className="p-4">
            <Message
              content={getLastAssistantMessage()}
              role={'assistant' as const}
              isCollapsed={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWindow; 